import { Component, OnDestroy, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, UntypedFormControl, UntypedFormGroup, Validators } from '@angular/forms';
import { ModalController, NavParams } from '@ionic/angular';
import { Subject, debounceTime, mapTo, merge, takeUntil } from 'rxjs';
import { TIME_OF_DAY_DESCRIPTION } from 'src/app/pages/agreements/constants/rate-description.constants';
import {
  PriceLimitAlertEnum,
  PriceLimitAlertToDEnum,
} from 'src/app/pages/agreements/enums/price-limit-alert-type.enum';
import { AgreementsService } from 'src/app/pages/agreements/services/agreements.service';
import { RateChecker } from 'src/app/shared/models/gql/rate-checker.model';
import { BudgetSupportType, RateSchedule } from 'src/app/shared/models/gql/rate.model';
import { NetRate, PriceLimitAlertData } from 'src/app/shared/models/price-limit-alert-data.model';
import { LoaderService } from 'src/app/shared/services/loader.service';
import { UtilityService } from 'src/app/shared/services/utility.service';
import { getGraphQLErrorText } from 'src/app/shared/utilities/graphql-error-handling';
import { requireCheckboxesToBeCheckedValidator } from 'src/app/shared/validators/checkbox-required.validator';

@Component({
  selector: 'app-rate-details',
  templateUrl: './rate-details.component.html',
  styleUrls: ['./rate-details.component.scss'],
})
export class RateDetailsComponent implements OnInit, OnDestroy {
  // Rate details form group
  rateDetailsForm: FormGroup;

  // Show overnight banner
  showOvernightBanner = false;

  // Default rate schedule
  private defaultRateSchedule: RateSchedule = {
    weekday: false,
    saturday: false,
    sunday: false,
    publicHoliday: false,
    daytime: false,
    evening: false,
    overnight: false,
  };

  // Rate type checkbox options
  rateTypeOptions: Array<{ id: boolean; name: string; isChecked: boolean }> = [
    { name: 'Hourly rate', id: false, isChecked: false },
    { name: 'Flat rate', id: true, isChecked: false },
  ];

  // Days this rate is for checkbox options
  dayOfWeekOptions: Array<{ id: string; name: string; isChecked: boolean }> = [
    { id: 'weekday', name: 'Weekday (Mon–Fri)', isChecked: false },
    { id: 'saturday', name: 'Saturday', isChecked: false },
    { id: 'sunday', name: 'Sunday', isChecked: false },
    { id: 'publicHoliday', name: 'Public Holiday', isChecked: false },
  ];

  // Times this rate is for checkbox options
  timeOfDayOptions: Array<{ id: string; name: string; isChecked: boolean }> = [
    { id: 'daytime', name: 'Daytime (ends between 6am – 8pm)', isChecked: false },
    { id: 'evening', name: 'Evening (ends between 8pm – Midnight)', isChecked: false },
    { id: 'overnight', name: 'Overnight active (starts and/or ends between Midnight – 6am)', isChecked: false },
  ];

  // Rate name
  private rateName = '';

  // Rate description
  private rateDescription = '';

  // Rate schedule
  private rateSchedule = this.defaultRateSchedule;

  // Flat rate
  private flatRate = false;

  // net rate
  private netRate: number;

  // minium hourly rate
  minimumHourlyRate: number;

  // checkbox options for budget support type
  allBudgetSupportTypesOptions = [];

  // available budget support types;
  private availableBudgetSupportTypes: BudgetSupportType[] = [];

  // posted job id
  private postedJobId: string | number;

  // agreement's grandfathered date (null in case it has not been grandfathered)
  private agreementGrandfatheredAt: Date | null;

  // data to be passed to the price limit alert component for segment event use
  priceLimitAlertData: PriceLimitAlertData;

  // consumer id
  private consumerId: number;

  // consumer uuid
  private consumerUuid: string;

  // support provider uuid
  private supportProviderUuid: string;

  // rate cents
  private rateCents: number;

  // flag to indicate whether the rate is being created or edited
  private isNewRate = false;

  // string that will be displayed on the price limit alert
  private priceLimitAlertType = '';

  // location where support is provided
  private supportLocation = '';

  // support type
  private supportType = '';

  // support name
  private supportName = '';

  // flag to indicate to whether to display or not info about net rate on InputComponent
  shouldDisplayNewRateInfo = true;

  constructor(
    protected modalCtrl: ModalController,
    private formBuilder: FormBuilder,
    public loaderService: LoaderService,
    private navParams: NavParams,
    private utilityService: UtilityService,
    private agreementsService: AgreementsService,
  ) {}

  onDestroySubject = new Subject<void>();

  /**
   * Rate type control getter.
   */
  get rateTypeControl() {
    return this.rateDetailsForm.controls.rateType;
  }

  /**
   * Day of week group getter.
   */
  get dayOfWeekGroup() {
    return this.rateDetailsForm.get('dayOfWeekGroup');
  }

  /**
   * Time of day group getter.
   */
  get timeOfDayGroup() {
    return this.rateDetailsForm.get('timeOfDayGroup');
  }

  /**
   * ShowCapRateBanner getter - flag to display or not the cap rate banner
   */
  get showCapRateBanner(): boolean {
    return this.rateDetailsForm.get('showCapRateBanner').value;
  }

  /**
   * Budget support type group getter.
   */
  get budgetSupportTypeGroup() {
    return this.rateDetailsForm.get('budgetSupportTypeGroup');
  }

  /**
   * Getter that evaluates whether the rate amount control is invalid
   */
  get isRateAmountInvalid(): boolean {
    return (
      this.rateDetailsForm.controls.rateAmount.errors?.max ||
      this.rateDetailsForm.controls.rateAmount.errors?.min ||
      this.rateDetailsForm.controls.rateAmount.errors?.pattern ||
      (this.rateDetailsForm.controls.rateAmount.errors?.required && this.rateDetailsForm.controls.rateAmount.touched)
    );
  }

  ngOnInit(): void {
    this.initiliaseProperties();
    this.setupFormGroup();
    this.setFormValues();
    this.setupSupportTypes();
    this.listenFormValueChange();
  }

  /**
   * Update the subject on destroying the component.
   */
  ngOnDestroy(): void {
    this.onDestroySubject.next();
    this.onDestroySubject.complete();
  }

  /**
   * This is triggered when Time of Day checkboxes are checked/unchecked. It updates the timeOfDayGroup form group
   * and also sets the showOvernightBanner variable based on whether the overnight check box is ticked or not.
   */
  onTimeOfDayChange(): void {
    this.rateDetailsForm.get('timeOfDayGroup')?.setValue(
      Object.assign(
        {},
        ...this.timeOfDayOptions.map((option) => {
          if (option.id === 'overnight') {
            this.showOvernightBanner = option.isChecked;
          }
          return { [option.id]: option.isChecked };
        }),
      ),
    );
  }

  /**
   * This is triggered when Day of Week checkboxes are checked/unchecked. It updates the dayOfWeekGroup form group.
   */
  onDayOfWeekChange(): void {
    this.rateDetailsForm
      .get('dayOfWeekGroup')
      ?.setValue(Object.assign({}, ...this.dayOfWeekOptions.map((option) => ({ [option.id]: option.isChecked }))));
  }

  /**
   * This is triggered when Budget Support Types checkboxes are checked/unchecked. It updates the budgetSupportTypeGroup form group.
   */
  onBudgetSelectionChange() {
    this.rateDetailsForm
      .get('budgetSupportTypeGroup')
      ?.setValue(
        Object.assign({}, ...this.allBudgetSupportTypesOptions.map((option) => ({ [option.id]: option.isChecked }))),
      );

    const supportTypes = this.getSelectedBudgetSupportTypes();

    this.rateDetailsForm.patchValue({
      budgetSupportTypes: supportTypes,
    });
  }

  /**
   * Triggered on form submission.
   */
  onSubmit(): void {
    if (!this.rateDetailsForm.valid) {
      this.rateDetailsForm.markAllAsTouched();
      // workaround to display an error message for the rate cents field in case user tries to submit the form without filling it.
      this.rateDetailsForm.controls.rateAmount.markAsTouched();
      return;
    }
    const formValue = this.rateDetailsForm.value;
    const rateSchedule = formValue.rateType ? this.defaultRateSchedule : this.getRateScheduleFromOptions();

    const supportTypes = this.getSelectedBudgetSupportTypes();

    const data = {
      flatRate: formValue.rateType,
      rateDescription: formValue.rateDescription,
      rateName: formValue.rateName,
      rateSchedule,
      displaySegment: false,
      budgetSupportTypes: supportTypes,
      rateCents: formValue.rateAmount,
      showCapRateBanner: formValue.showCapRateBanner,
    };
    const olderData = {
      flatRate: this.flatRate,
      rateDescription: this.rateDescription,
      rateName: this.rateName,
      rateSchedule: this.rateSchedule,
      displaySegment: false,
      budgetSupportTypes: this.navParams.get('budgetSupportTypes'),
      rateCents: this.rateCents,
      showCapRateBanner: this.showCapRateBanner,
    };
    // compare older and newer data, to call segment event only if there are any changes
    if (JSON.stringify(data) !== JSON.stringify(olderData)) {
      data.displaySegment = true;
    }

    this.modalCtrl.dismiss(data);
  }

  /**
   * Opens Qualtrics form.
   * This form is needed when SP wants to request a non-standard budget support item to be added to a rate.
   */
  openQualtricsForm(): void {
    const qualtrixFormUrl = `https://mable.qualtrics.com/jfe/form/SV_86NlaKRhys8PP4q?origin=platform&s=${this.supportProviderUuid}&c=${this.consumerUuid}`;
    this.utilityService.openExternalLink(qualtrixFormUrl);
  }

  /**
   * Opens a webpage where SP will find more information about service categories.
   * Also, it sends a segment event.
   */
  openMoreInfoAboutServiceCategoriesLink(): void {
    this.utilityService.openExternalLink('https://mable.com.au/help_centre/NDIS-support-items/');
  }

  /**
   * Setup the rate details form.
   */
  private setupFormGroup(): void {
    this.rateDetailsForm = this.formBuilder.group({
      rateType: [false, [Validators.required]],
      rateName: ['', [Validators.required]],
      rateDescription: [''],
      dayOfWeekGroup: this.formBuilder.group(
        {
          weekday: [false],
          saturday: [false],
          sunday: [false],
          publicHoliday: [false],
        },
        { validators: requireCheckboxesToBeCheckedValidator() },
      ),
      timeOfDayGroup: this.formBuilder.group({
        daytime: [false],
        evening: [false],
        overnight: [false],
      }),
      budgetSupportTypeGroup: this.formBuilder.group({}),
      rateAmount: [
        0,
        [
          Validators.max(9999.99),
          Validators.required,
          Validators.pattern('^[0-9]+(.[0-9]{0,2})?$'),
          Validators.min(this.flatRate ? 1 : this.minimumHourlyRate),
        ],
      ],
      netRate: [0],
      capRate: [0],
      showCapRateBanner: [false],
      budgetSupportTypes: [],
      priceLimitAlertType: [''],
      supportLocation: [''],
      supportType: [''],
      supportName: [''],
    });
  }

  /**
   * Patch the rate details form values.
   */
  private setFormValues(): void {
    this.rateDetailsForm.patchValue({
      rateType: this.flatRate,
      rateAmount: this.rateCents,
      rateName: this.rateName,
      rateDescription: this.rateDescription,
      netRate: this.netRate,
      budgetSupportTypes: [],
      priceLimitAlertType: [this.priceLimitAlertType],
      supportLocation: [this.supportLocation],
      supportType: [this.supportType],
      supportName: [this.supportName],
    });

    if (!this.flatRate) {
      const { weekday, saturday, sunday, publicHoliday, daytime, evening, overnight } = this.rateSchedule
        ? this.rateSchedule
        : this.defaultRateSchedule;

      this.rateDetailsForm.patchValue({
        dayOfWeekGroup: {
          weekday,
          saturday,
          sunday,
          publicHoliday,
        },
        timeOfDayGroup: { daytime, evening, overnight },
      });
    } else {
      this.dayOfWeekGroup.clearValidators();
    }

    this.dayOfWeekGroup.updateValueAndValidity();
    this.updateDayOfWeekOptions();
    this.updateTimeOfDayOptions();
  }

  /**
   * Subscribe to form value changes to update the validations.
   */
  private listenFormValueChange(): void {
    this.onDestroySubject.next();
    this.shouldDisplayNewRateInfo = !this.isNewRate || this.rateDetailsForm?.get('rateAmount')?.touched;

    // On change of rateType control, insert/remove validations
    this.rateTypeControl.valueChanges.pipe(takeUntil(this.onDestroySubject)).subscribe((value) => {
      if (value === false) {
        this.dayOfWeekGroup.setValidators([requireCheckboxesToBeCheckedValidator()]);
        this.rateDetailsForm
          .get('rateAmount')
          .setValidators([
            Validators.required,
            Validators.pattern('^[0-9]+(.[0-9]{0,2})?$'),
            Validators.min(this.minimumHourlyRate),
            Validators.max(9999.99),
          ]);
        if (this.allBudgetSupportTypesOptions.length)
          this.budgetSupportTypeGroup.setValidators([requireCheckboxesToBeCheckedValidator()]);
      } else {
        this.dayOfWeekGroup.clearValidators();
        this.budgetSupportTypeGroup.clearValidators();
        this.rateDetailsForm
          .get('rateAmount')
          .setValidators([
            Validators.required,
            Validators.pattern('^[0-9]+(.[0-9]{0,2})?$'),
            Validators.min(1),
            Validators.max(9999.99),
          ]);
      }
      this.dayOfWeekGroup.updateValueAndValidity();
      this.budgetSupportTypeGroup.updateValueAndValidity();
      this.rateDetailsForm.get('rateAmount').updateValueAndValidity();
    });
    const controlsToListen = [];
    controlsToListen.push(
      this.rateDetailsForm.controls.budgetSupportTypeGroup,
      this.rateDetailsForm.controls.dayOfWeekGroup,
      this.rateDetailsForm.controls.timeOfDayGroup,
      this.rateDetailsForm.controls.rateAmount,
    );

    // listen to each control
    merge(
      ...controlsToListen.map((control) =>
        control.valueChanges.pipe(mapTo(control), debounceTime(200), takeUntil(this.onDestroySubject)),
      ),
    ).subscribe(async () => {
      this.shouldDisplayNewRateInfo = !this.isNewRate || this.rateDetailsForm?.get('rateAmount')?.touched;
      await this.getCalculatedRate();
    });
  }

  /**
   * Returns the rateSchedule from the dayOfWeekOptions and timeOfDayOptions.
   *
   * @returns an object containing the rate schedule.
   */
  private getRateScheduleFromOptions(): RateSchedule {
    const rateScheduleArray = this.dayOfWeekOptions.concat(this.timeOfDayOptions);

    return Object.assign({}, ...rateScheduleArray.map((option) => ({ [option.id]: option.isChecked })));
  }

  /**
   * Updates the day of week options from the dayOfWeekGroup form group.
   */
  private updateDayOfWeekOptions(): void {
    this.dayOfWeekOptions.forEach((option) => {
      option.isChecked = this.dayOfWeekGroup.value[option.id];
    });
  }

  /**
   * Updates the time of day options from the timeOfDayGroup form group.
   */
  private updateTimeOfDayOptions(): void {
    this.timeOfDayOptions.forEach((option) => {
      option.isChecked = this.timeOfDayGroup.value[option.id];
    });
  }

  /**
   * Set support type checkbox list and set controllers in formcontrol
   */
  private setupSupportTypes(): void {
    const defaultBudgetSupportTypes: BudgetSupportType[] = this.navParams.get('budgetSupportTypes');
    const budgetSupportTypeGroup = this.rateDetailsForm.controls.budgetSupportTypeGroup as UntypedFormGroup;
    this.allBudgetSupportTypesOptions = this.availableBudgetSupportTypes.map((support) => {
      const isIdExists = defaultBudgetSupportTypes.some((defaultOption) => defaultOption.id === support.id);
      budgetSupportTypeGroup.addControl(support.id, new UntypedFormControl(isIdExists));
      return {
        id: support.id,
        name: support.displayName,
        isChecked: isIdExists,
      };
    });

    // Set validators if budgetSupportTypes available
    if (this.allBudgetSupportTypesOptions && this.allBudgetSupportTypesOptions.length > 0) {
      budgetSupportTypeGroup.setValidators([requireCheckboxesToBeCheckedValidator()]);
      this.budgetSupportTypeGroup.updateValueAndValidity();
    }
  }

  /**
   * Function that call request to calculate rate and update the value with response.
   *
   * @param rateFormGroup - The rate in form group that will be updated with the rate in API resposne.
   */
  private async getCalculatedRate(): Promise<void> {
    const formValue = this.rateDetailsForm.value;
    const selectedBudgetSupportIds = Object.keys(formValue.budgetSupportTypeGroup).filter(
      (id) => formValue.budgetSupportTypeGroup[id] === true,
    );

    // Avoid api call in case of invalid field data
    if (!this.rateDetailsForm.get('rateAmount').valid || !this.rateDetailsForm?.get('rateAmount')?.value) {
      this.shouldDisplayNewRateInfo = false;
      this.rateDetailsForm.patchValue({
        showCapRateBanner: false,
      });
      return;
    }

    const timeAndPlace = {
      rateSchedule: this.rateDetailsForm.value.rateType ? this.defaultRateSchedule : this.getRateScheduleFromOptions(),
    };

    let requestObject: RateChecker = {
      baseRate: parseFloat(this.rateDetailsForm?.get('rateAmount')?.value),
      consumerId: this.consumerId,
      grandfatheredAt: this.agreementGrandfatheredAt,
      timeAndPlace,
    };

    const includeBudgetSupportTypes = selectedBudgetSupportIds && selectedBudgetSupportIds.length > 0;
    if (includeBudgetSupportTypes) {
      requestObject = {
        ...requestObject,
        budgetSupportTypes: selectedBudgetSupportIds,
        postedJobId: this.postedJobId,
      };
    }
    try {
      const netRate: NetRate = await this.agreementsService.getRates(requestObject);
      // check if net rate is exists
      if (netRate) {
        // get rate controls and update the rate from api response
        this.rateDetailsForm.patchValue({
          netRate: netRate.rateWithFee,
          showCapRateBanner: netRate.exceedCapRate,
          capRate: netRate.capRate,
          supportLocation: netRate.supportLocation,
          supportType: netRate.supportType,
          supportName: netRate.supportName,
          priceLimitAlertType: this.getPriceLimitAlertType(netRate.priceLimitAlertType, netRate.priceLimitAlertToDType),
        });
      }
    } catch (error) {
      this.utilityService.showErrorAlert(getGraphQLErrorText(error));
    }
  }

  /**
   * It initialises the component properties based on the componentProps passed
   * when this modal was called.
   */
  private initiliaseProperties() {
    this.netRate = this.navParams.get('netRate');
    this.rateCents = this.navParams.get('rateCents');
    this.rateSchedule = this.navParams.get('rateSchedule') || this.defaultRateSchedule;
    this.rateDescription = this.navParams.get('rateDescription') || '';
    this.flatRate = this.navParams.get('flatRate') || false;
    this.rateName = this.navParams.get('rateName') || '';
    this.agreementGrandfatheredAt = this.navParams.get('agreementGrandfatheredAt') || null;
    this.priceLimitAlertData = this.navParams.get('priceLimitAlertData');
    this.minimumHourlyRate = this.navParams.get('minimumHourlyRate');
    this.postedJobId = this.navParams.get('postedJobId') || null;
    this.availableBudgetSupportTypes = this.navParams.get('availableBudgetSupportTypes') || null;
    this.isNewRate = this.navParams.get('isNewRate') || false;
    this.consumerId = this.navParams.get('consumerId') || null;
    this.consumerUuid = this.navParams.get('consumerUuid');
    this.supportProviderUuid = this.navParams.get('supportProviderUuid');
    this.priceLimitAlertType = this.navParams.get('priceLimitAlertType') || null;
    this.supportLocation = this.navParams.get('supportLocation') || '';
    this.supportType = this.navParams.get('supportType') || '';
    this.supportName = this.navParams.get('supportName') || '';
  }

  /**
   * Returns a string representing what should be displayed on the price limit alert.
   *
   * @param dayOfWeek - priceLimitAlertType passed from the backend indicating day of the week.
   * @param timeOfDay - priceLimitAlertTodType passed from the backend indicating time of the day.
   * @returns - String representation of what the price limit alert type refers to.
   */
  private getPriceLimitAlertType(dayOfWeek: PriceLimitAlertEnum, timeOfDay: PriceLimitAlertToDEnum): string {
    const showBudgetSupportTypes = !!(this.allBudgetSupportTypesOptions && this.allBudgetSupportTypesOptions?.length);
    if (timeOfDay) {
      return `${dayOfWeek}, ${TIME_OF_DAY_DESCRIPTION[timeOfDay.toLocaleLowerCase()]}${
        showBudgetSupportTypes ? '' : '.'
      }`;
    }

    return `${dayOfWeek}${showBudgetSupportTypes ? '' : '.'}` || 'Weekday between 6am - 8pm';
  }

  /**
   * It returns which budget support type(s) has/have been selected by SP.
   *
   * @returns budget support type(s) has/have been selected by SP.
   */
  private getSelectedBudgetSupportTypes(): BudgetSupportType[] {
    const formValue = this.rateDetailsForm.value;
    const selectedBudgetSupportIds = Object.keys(formValue.budgetSupportTypeGroup).filter(
      (id) => formValue.budgetSupportTypeGroup[id] === true,
    );
    return this.availableBudgetSupportTypes.filter((support) => selectedBudgetSupportIds.includes(support.id));
  }
}
